// Custom Link to intercept responses and attach them to the operation
import {ApolloClient, ApolloLink, createHttpLink, split} from '@apollo/client/core';
import {GraphQLWsLink} from '@apollo/client/link/subscriptions';
import {getMainDefinition} from '@apollo/client/utilities';
import {createClient} from 'graphql-ws';
import {onError} from '@apollo/client/link/error';
import cache from '@/shared/apolloclient/apollo.cache.ts';
import authRetryLink from '@/shared/apolloclient/apollo.auth.ts';
import {useErrorStore} from '@/shared/stores/network.store.ts';
import * as Sentry from '@sentry/vue';
import {useMaintenanceStore} from "@/shared/stores/maintenance.store.ts";

const responseInterceptor = new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
        // Attach response to the operation context
        operation.setContext({response});
        return response;
    });
});

const errorLink = onError(({graphQLErrors, networkError, operation}) => {
    // Extract the response from the context
    const {response} = operation.getContext();

    if (graphQLErrors) {
        graphQLErrors.forEach(({message, locations, path}) => {
            console.log(`[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`);

            try {
                // Capture GraphQL errors with Sentry
                Sentry.captureException(new Error(message), {
                    extra: {
                        type: 'GraphQL Error',
                        operationName: operation.operationName,
                        query: operation.query.loc?.source.body, // The full GraphQL query
                        variables: operation.variables,         // Query variables
                        response: response || null,             // Include the response, if it exists
                        locations,
                        path,
                    },
                });
            } catch (e) {
                console.log('Could not log Sentry error', e);
            }
        });
    }

    if (networkError) {
        // Handle and log 403 errors specifically
        //@ts-ignore
        if (networkError.response && networkError.response.status === 403) {
            const errorStore = useErrorStore();
            errorStore.setAccessDeniedError(true);
        } else {
            console.log(`[Network error]: ${networkError}`);

            try {
                // Capture network errors with Sentry
                Sentry.captureException(networkError, {
                    extra: {
                        type: 'Network Error',
                        operationName: operation.operationName,
                        query: operation.query.loc?.source.body, // The full GraphQL query
                        variables: operation.variables,         // Query variables
                        response: response || null,             // Include the response, even for network errors
                        //@ts-ignore
                        status: networkError.statusCode,        // HTTP status code
                    },
                });
            } catch (e) {
                console.log('Could not log Sentry error', e);
            }
        }
    }
});

const wsLink = new GraphQLWsLink(
    createClient({
        url: import.meta.env.VITE_GRAPHQL_SOCKET_API,
        retryAttempts: 10,
        shouldRetry: () => true, // Retry even on "fatal" errors
        retryWait: async (count) => {
            // Function to check internet connectivity
            const isOnline = () => navigator.onLine;

            // Handle case when internet is down
            if (!isOnline()) {
                console.log('Internet connection lost, waiting for reconnection...');
                return new Promise<void>((resolve) => {
                    const checkInternet = () => {
                        if (isOnline()) {
                            console.log('Internet connection restored');
                            window.removeEventListener('online', checkInternet);
                            resolve();
                        } else {
                            console.log('Still offline, checking again in 1s');
                        }
                    };
                    window.addEventListener('online', checkInternet);
                });
            }

            // Normal reconnection logic (exponential backoff)
            const delay = Math.min(1000 * Math.pow(2, count), 10000);
            console.log(`Reconnecting in ${delay}ms (attempt ${count + 1} of 10)`);
            return new Promise<void>((resolve) => {
                setTimeout(() => {
                    console.log(`Retry attempt ${count + 1} starting now`);
                    resolve();
                }, delay);
            });
        },
        keepAlive: 10000, // Send ping every 10s
        on: {
            connected: (socket) => {
                console.log('WebSocket connected successfully');
                const maintenanceStore = useMaintenanceStore();
                if (maintenanceStore.maintenance) {
                    console.log('Connection restored after maintenance, disabling maintenance mode');
                    maintenanceStore.maintenance = false;
                }
            },
            closed: (event) => {
                console.log('WebSocket closed:', event);
                //@ts-ignore
                console.log(`Close code: ${event.code}, Was clean: ${event.wasClean}, Reason: "${event.reason}"`);
            },
            error: (err) => {
                //@ts-ignore
                console.error('WebSocket error:', err.message, err);
            },
            ping: (received) => {
                if (!received) console.debug('Sent ping to server');
            },
            pong: (received) => {
                if (received) console.debug('Received pong from server');
            },
            connecting: () => console.log('Attempting to connect to WebSocket'),
        },
    })
);
const httpLink = createHttpLink({
    //@ts-ignore
    uri: import.meta.env.VITE_GRAPHQL_API,
    credentials: 'include',
    preserveHeaderCase: true,
});

/**
 *  The split function takes three parameters:
 *  A function that's called for each operation to execute
 *  The Link to use for an operation if the function returns a "truthy" value
 *  The Link to use for an operation if the function returns a "falsy" value
 */
const splitLink = split(
    ({query}) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    httpLink
);

export function createApolloClient() {
    return new ApolloClient({
        cache,
        // link: responseInterceptor.concat(errorLink).concat(authRetryLink.concat(authErrorLink).concat(splitLink)),
        link: responseInterceptor.concat(errorLink).concat(authRetryLink.concat(splitLink)),
        devtools: {
            enabled: true,
            name: "Marcando"
        },
    })
}


let apolloClient = createApolloClient();

export function getApolloClient(): ApolloClient<any> {
    return apolloClient;
}

export async function restartApollo(): Promise<void> {
    getApolloClient().stop();
    apolloClient = createApolloClient();
}

